import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  Input,
  Tag,
  Modal,
  Form,
  Select,
  message,
  Collapse,
  Spin,
  Empty,
  Card,
  Row,
  Col,
  Popover,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LinkOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import useAdminData from "../hooks/useAdminData";

const { Option } = Select;

const STAFF_ROLES = {
  SUPPORT_EXECUTIVE: "Support Executive",
  SUPPORT_MANAGER: "Support Manager",
  SUPPORT_OFFICER: "Support Officer",
};

const AdminSupportManagers = () => {
  const {
    supportStaffs,
    loading,
    error,
    fetchSupportStaffs,
    createSupportStaff,
    updateSupportStaff,
    deleteSupportStaff,
    assignZoneToStaff,
    lookupUserByAccountId,
    zones,
    fetchZones,
  } = useAdminData();

  const userProfile = useSelector((state) => state.userProfile?.user);
  const userRole = userProfile?.role?.toUpperCase() || "";
  const isAdminOrSE = ["ADMIN", "SUPPORT_EXECUTIVE"].includes(userRole);

  const staffList = supportStaffs?.data || supportStaffs || [];

  const [searchText, setSearchText] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);
  const [filteredStaffs, setFilteredStaffs] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [form] = Form.useForm();
  const [assignZoneModalVisible, setAssignZoneModalVisible] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [filterPopoverVisible, setFilterPopoverVisible] = useState(false);
  const [formValues, setFormValues] = useState({});

  // Account lookup states
  const [accountIdInput, setAccountIdInput] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookedUpUser, setLookedUpUser] = useState(null);
  const [createMethod, setCreateMethod] = useState("lookup"); // "lookup" or "manual"

  useEffect(() => {
    const filtered = staffList.filter((staff) => {
      const matchesSearch =
        `${staff.firstName || ""} ${staff.lastName || ""}`
          .toLowerCase()
          .includes(searchText.toLowerCase()) ||
        staff.email?.toLowerCase().includes(searchText.toLowerCase());
      const matchesRole = selectedRole ? staff.role === selectedRole : true;
      return matchesSearch && matchesRole;
    });
    setFilteredStaffs(filtered);
  }, [staffList, searchText, selectedRole]);

  const handleCreate = () => {
    setEditingStaff(null);
    setCreateMethod("lookup");
    setLookedUpUser(null);
    setAccountIdInput("");
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleLookupByAccountId = async () => {
    if (!accountIdInput || accountIdInput.length !== 8) {
      message.warning("Please enter a valid 8-digit account ID");
      return;
    }

    try {
      setLookupLoading(true);
      const userData = await lookupUserByAccountId(accountIdInput);
      if (userData) {
        setLookedUpUser(userData);
        form.setFieldsValue({
          firstName: userData.firstName || userData.first_name,
          lastName: userData.lastName || userData.last_name,
          email: userData.email,
          phoneNumber:
            userData.phoneNumber || userData.phone || userData.phone_number,
        });
        message.success("User found successfully");
      } else {
        message.error("User not found with this account ID");
      }
    } catch (err) {
      message.error(err.message || "Failed to lookup user");
    } finally {
      setLookupLoading(false);
    }
  };

  const handleClearLookup = () => {
    setLookedUpUser(null);
    setAccountIdInput("");
    form.resetFields();
  };

  const handleEdit = (staff) => {
    setEditingStaff(staff);
    form.setFieldsValue({
      firstName: staff.firstName,
      lastName: staff.lastName,
      email: staff.email,
      phoneNumber: staff.phoneNumber,
      role: staff.role,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (staffId) => {
    Modal.confirm({
      title: "Delete Support Staff",
      content: "Are you sure you want to delete this support staff member?",
      onOk: async () => {
        try {
          await deleteSupportStaff(staffId);
          message.success("Support staff deleted successfully");
        } catch (error) {
          message.error("Failed to delete support staff");
        }
      },
    });
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (editingStaff) {
        await updateSupportStaff(editingStaff.id, values);
        message.success("Support staff updated successfully");
      } else {
        // If creating from lookup mode, include the accountId
        const staffData =
          createMethod === "lookup" && lookedUpUser
            ? { ...values, accountId: accountIdInput }
            : values;

        await createSupportStaff(staffData);
        message.success("Support staff created successfully");
      }
      setIsModalVisible(false);
      // Reset lookup states
      setLookedUpUser(null);
      setAccountIdInput("");
      setCreateMethod("lookup");
    } catch (error) {
      message.error("Operation failed");
    }
  };

  const handleAssignZone = (staff) => {
    if (staff.role === "SUPPORT_EXECUTIVE") {
      message.info("Support Executives manage all zones");
      return;
    }
    setSelectedStaff(staff);
    setSelectedZoneId(null);
    setAssignZoneModalVisible(true);
  };

  const handleAssignZoneOk = async () => {
    if (!selectedZoneId) {
      message.warning("Please select a zone");
      return;
    }
    try {
      await assignZoneToStaff(selectedStaff.id, selectedZoneId);
      message.success("Zone assigned successfully");
      setAssignZoneModalVisible(false);
    } catch (error) {
      message.error("Failed to assign zone");
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "SUPPORT_EXECUTIVE":
        return "gold";
      case "SUPPORT_MANAGER":
        return "blue";
      case "SUPPORT_OFFICER":
        return "green";
      default:
        return "default";
    }
  };

  // Check if form is valid for submission
  const isFormValid = () => {
    if (editingStaff) {
      return true; // Editing always allowed
    }

    if (createMethod === "lookup") {
      // For lookup: need accountId (8 digits) and role
      return accountIdInput.length === 8 && formValues?.role;
    } else {
      // For manual: need all fields
      const { firstName, lastName, email, phoneNumber, role } =
        formValues || {};
      return firstName && lastName && email && phoneNumber && role;
    }
  };

  const staffCollapseItems = filteredStaffs.map((staff, idx) => ({
    key: idx,
    label: (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <div>
          <p style={{ margin: 0, fontWeight: 600 }}>
            {staff.firstName} {staff.lastName}
          </p>
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <Tag color={getRoleColor(staff.role)}>
            {STAFF_ROLES[staff.role] || staff.role}
          </Tag>
          {/* {staff.role !== "SUPPORT_EXECUTIVE" && (
            <Tag color={staff.zone ? "blue" : "default"}>
              {staff.zone ? staff.zone.name : "Unassigned"}
            </Tag>
          )} */}
        </div>
      </div>
    ),
    children: (
      <div style={{ paddingTop: "16px" }}>
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <p style={{ margin: "0 0 8px 0", fontSize: "12px", color: "#999" }}>
              Phone
            </p>
            <p style={{ margin: 0 }}>
              {staff.phoneCode}
              {staff.phoneNumber || "N/A"}
            </p>
          </Col>
          <Col xs={24}>
            <p style={{ margin: "0 0 8px 0", fontSize: "12px", color: "#999" }}>
              Email
            </p>
            <p style={{ margin: 0 }}>{staff.email}</p>
          </Col>
          <Col xs={24}>
            <p style={{ margin: "0 0 8px 0", fontSize: "12px", color: "#999" }}>
              Role
            </p>
            <p style={{ margin: 0 }}>
              <Tag color={getRoleColor(staff.role)}>
                {STAFF_ROLES[staff.role] || staff.role}
              </Tag>
            </p>
          </Col>
          {staff.role !== "SUPPORT_EXECUTIVE" &&
            (staff.zone ? (
              <Col xs={24}>
                <p
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "12px",
                    color: "#999",
                  }}
                >
                  Assigned Zone
                </p>
                <p style={{ margin: 0 }}>{staff.zone.name}</p>
              </Col>
            ) : (
              <Col xs={24}>
                <p
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "12px",
                    color: "#999",
                  }}
                >
                  Assigned Zone
                </p>
                <p style={{ margin: 0, color: "#999" }}>Unassigned</p>
              </Col>
            ))}
          <Col xs={24}>
            {isAdminOrSE && (
              <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
                <Button
                  type="primary"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(staff)}
                >
                  Edit
                </Button>
                {staff.role !== "SUPPORT_EXECUTIVE" && (
                  <Button
                    type="default"
                    size="small"
                    icon={<LinkOutlined />}
                    onClick={() => handleAssignZone(staff)}
                  >
                    Assign Zone
                  </Button>
                )}
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(staff.id)}
                >
                  Delete
                </Button>
              </div>
            )}
          </Col>
        </Row>
      </div>
    ),
  }));

  return (
    <div>
      <Spin spinning={loading}>
        <div className="pb-40">
          {error && (
            <Card className="mb-6 bg-red-50 border-red-200">
              <p className="text-red-600">{error}</p>
            </Card>
          )}

          <div
            className="mb-6"
            style={{ display: "flex", gap: "8px", alignItems: "center" }}
          >
            <Input
              placeholder="Search by name or email..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{ flex: 1 }}
              prefix={<SearchOutlined style={{ color: "#999" }} />}
            />
            <Popover
              content={
                <div style={{ width: "180px" }}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                        fontSize: "13px",
                      }}
                    >
                      <input
                        type="radio"
                        checked={selectedRole === null}
                        onChange={() => setSelectedRole(null)}
                      />
                      All Roles
                    </label>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                        fontSize: "13px",
                      }}
                    >
                      <input
                        type="radio"
                        checked={selectedRole === "SUPPORT_EXECUTIVE"}
                        onChange={() => setSelectedRole("SUPPORT_EXECUTIVE")}
                      />
                      Support Executive
                    </label>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                        fontSize: "13px",
                      }}
                    >
                      <input
                        type="radio"
                        checked={selectedRole === "SUPPORT_MANAGER"}
                        onChange={() => setSelectedRole("SUPPORT_MANAGER")}
                      />
                      Support Manager
                    </label>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                        fontSize: "13px",
                      }}
                    >
                      <input
                        type="radio"
                        checked={selectedRole === "SUPPORT_OFFICER"}
                        onChange={() => setSelectedRole("SUPPORT_OFFICER")}
                      />
                      Support Officer
                    </label>
                  </div>
                </div>
              }
              trigger="click"
              open={filterPopoverVisible}
              onOpenChange={setFilterPopoverVisible}
              placement="bottomLeft"
            >
              <Button
                icon={<FilterOutlined />}
                type={selectedRole ? "primary" : "default"}
                style={{ minWidth: "40px" }}
              />
            </Popover>
            {isAdminOrSE && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
                style={{ whiteSpace: "nowrap" }}
              >
                Add Staff
              </Button>
            )}
          </div>

          <Card bordered={false} className="mb-6">
            <h3 className="font-bold mb-6">
              Support Staff Members ({filteredStaffs.length})
            </h3>
            {filteredStaffs.length === 0 ? (
              <Empty description="No support staff found" />
            ) : (
              <Collapse items={staffCollapseItems} defaultActiveKey={[]} />
            )}
          </Card>
        </div>
      </Spin>

      <Modal
        title={editingStaff ? "Edit Support Staff" : "Add Support Staff"}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setIsModalVisible(false);
          setLookedUpUser(null);
          setAccountIdInput("");
          setCreateMethod("lookup");
        }}
        okText={editingStaff ? "Update" : "Create"}
        okButtonProps={{ disabled: !isFormValid() }}
        width={createMethod === "lookup" && !lookedUpUser ? 450 : 500}
      >
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          onValuesChange={(changedValues, allValues) =>
            setFormValues(allValues)
          }
        >
          {/* Radio buttons to select creation method - only show when not editing and no user found */}
          {!editingStaff && !lookedUpUser && (
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  marginBottom: "8px",
                }}
              >
                <input
                  type="radio"
                  checked={createMethod === "lookup"}
                  onChange={() => {
                    setCreateMethod("lookup");
                    setLookedUpUser(null);
                    setAccountIdInput("");
                    form.resetFields();
                  }}
                />
                Lookup by Account ID
              </label>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="radio"
                  checked={createMethod === "manual"}
                  onChange={() => {
                    setCreateMethod("manual");
                    setLookedUpUser(null);
                    setAccountIdInput("");
                    form.resetFields();
                  }}
                />
                Manual Entry
              </label>
            </div>
          )}

          {/* Account ID Lookup Section - Only show for lookup mode and when no user found */}
          {createMethod === "lookup" && !lookedUpUser && (
            <div style={{ marginBottom: "24px" }}>
              <p style={{ marginBottom: "12px", color: "#666" }}>
                Enter the user's 8-digit account ID to pull their data
              </p>
              <div style={{ display: "flex", gap: "8px" }}>
                <Input
                  placeholder="Enter 8-digit account ID"
                  value={accountIdInput}
                  onChange={(e) => setAccountIdInput(e.target.value)}
                  maxLength={8}
                  style={{ flex: 1 }}
                  onPressEnter={handleLookupByAccountId}
                />
                <Button
                  type="primary"
                  onClick={handleLookupByAccountId}
                  loading={lookupLoading}
                >
                  Lookup
                </Button>
              </div>
            </div>
          )}

          {/* Show looked up user info - only display, no form fields */}
          {createMethod === "lookup" && lookedUpUser && (
            <div
              style={{
                marginBottom: "24px",
                padding: "16px",
                background: "#f5f5f5",
                borderRadius: "8px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "12px",
                }}
              >
                <Tag color="green">User Found</Tag>
                <Button size="small" onClick={handleClearLookup}>
                  Change
                </Button>
              </div>
              <p style={{ margin: "0 0 8px 0" }}>
                <strong>Name:</strong>{" "}
                {lookedUpUser.firstName || lookedUpUser.first_name}{" "}
                {lookedUpUser.lastName || lookedUpUser.last_name}
              </p>
              <p style={{ margin: "0 0 8px 0" }}>
                <strong>Email:</strong> {lookedUpUser.email}
              </p>
              <p style={{ margin: 0 }}>
                <strong>Phone:</strong>{" "}
                {lookedUpUser.phoneNumber ||
                  lookedUpUser.phone ||
                  lookedUpUser.phone_number}
              </p>
            </div>
          )}

          {/* Role field - shown for both lookup and manual modes */}
          <Form.Item
            label="Role"
            name="role"
            rules={[{ required: true, message: "Role is required" }]}
          >
            <Select placeholder="Select role">
              <Option value="SUPPORT_EXECUTIVE">Support Executive</Option>
              <Option value="SUPPORT_MANAGER">Support Manager</Option>
              <Option value="SUPPORT_OFFICER">Support Officer</Option>
            </Select>
          </Form.Item>

          {/* Manual Entry fields - only show for manual mode */}
          {createMethod === "manual" && (
            <>
              <Form.Item
                label="First Name"
                name="firstName"
                rules={[{ required: true, message: "First name is required" }]}
              >
                <Input placeholder="Enter first name" />
              </Form.Item>

              <Form.Item
                label="Last Name"
                name="lastName"
                rules={[{ required: true, message: "Last name is required" }]}
              >
                <Input placeholder="Enter last name" />
              </Form.Item>

              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: "Email is required" },
                  { type: "email", message: "Invalid email format" },
                ]}
              >
                <Input placeholder="Enter email" />
              </Form.Item>

              <Form.Item
                label="Phone Number"
                name="phoneNumber"
                rules={[
                  { required: true, message: "Phone number is required" },
                ]}
              >
                <Input placeholder="Enter phone number" />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      <Modal
        title={`Assign Zone to ${selectedStaff?.firstName} ${selectedStaff?.lastName}`}
        open={assignZoneModalVisible}
        onOk={handleAssignZoneOk}
        onCancel={() => setAssignZoneModalVisible(false)}
      >
        <Form layout="vertical">
          <Form.Item label="Zone" required>
            <Select
              placeholder="Select a zone"
              value={selectedZoneId}
              onChange={setSelectedZoneId}
              loading={loading}
            >
              {zones.map((zone) => (
                <Option key={zone.id} value={zone.id}>
                  {zone.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminSupportManagers;
